/**
 * RFP API Service Layer
 * Wraps core TraceOS APIs with RFP-specific helpers
 */

import { queryRAG } from '@tracekit/core';
import { submitReportJob, getReport, listReports, updateReportStatus } from '@tracekit/core';
import { uploadArtifact } from '@tracekit/core';
import { RAGQueryResponse, Citation } from '@tracekit/core';
import { getAPIClient } from '@tracekit/core';
import { Requirement, PastProposalMatch, OptimizationSuggestion, StaffMatchingRule, StaffMatchResult, ReportStaffAssignment, ReportStaffAssignmentsResponse } from '../types/rfp';

/**
 * Extract requirements from RFP document
 */
export async function extractRequirements(
  projectId: string,
  rfpArtifactId: string
): Promise<Requirement[]> {
  const response = await queryRAG(projectId, {
    question: `Extract all mandatory requirements from this RFP. For each requirement, provide:
      1. Requirement number (if numbered in RFP)
      2. Exact requirement text (quote)
      3. RFP section reference
      4. Page number
      Format as structured list.`,
    extraction_mode: 'quote', // Extract exact quotes
    require_evidence: true,
    max_results: 50,
    filters: {
      artifact_id: rfpArtifactId,
    },
  });

  if (response.status === 'needs_evidence') {
    throw new Error('Could not extract requirements - RFP may not be indexed yet');
  }

  // Format requirements from RAG response
  // RAG response has answer and citations
  const requirementsText = response.answer || '';
  const citations = response.citations || [];
  
  // Parse requirements from answer text
  // Look for numbered list items or structured format
  const requirementMatches = requirementsText.match(/\d+[.)]\s+(.+?)(?=\d+[.)]|$)/gs) || 
                              requirementsText.split('\n').filter(line => line.trim().match(/^\d+[.)]/));
  
  return requirementMatches.map((match, idx) => {
    // Extract requirement text
    const text = match.replace(/^\d+[.)]\s+/, '').trim();
    
    // Try to find section/page info in the text or use citation
    const sectionMatch = text.match(/Section\s+([\d.]+)/i);
    const pageMatch = text.match(/[Pp]age\s+(\d+)/);
    
    const citation = citations[idx] || citations[0];
    return {
      id: `req-${idx + 1}`,
      text: text.replace(/\s*\(Section.*?\)/gi, '').replace(/\s*\(Page.*?\)/gi, '').trim(),
      section: sectionMatch ? `Section ${sectionMatch[1]}` : (citation?.section_path || 'Unknown'),
      page: pageMatch ? parseInt(pageMatch[1]) : (citation?.page || null),
      confidence: citation?.confidence || response.evidence_confidence || response.confidence?.avg || 0.7,
      evidence_id: citation?.evidence_id || citation?.id?.toString() || null,
      keystone_response: '',
      status: 'Pending' as const,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
  });
}

/**
 * Search past proposals for similar work
 */
export async function searchPastProposals(
  projectId: string,
  query: string,
  maxResults: number = 20
): Promise<PastProposalMatch[]> {
  const response = await queryRAG(projectId, {
    question: query,
    extraction_mode: 'draft', // Allow lower confidence for discovery
    require_evidence: false,
    max_results: maxResults,
    filters: {
      source_type: 'historical_proposal',
    },
  });

  // RAG response structure: answer, citations, status
  // For proposal search, we need to parse the answer and match with citations
  const answer = response.answer || '';
  const citations = response.citations || [];
  
  // Parse answer to extract proposal references
  // In production, this would be more sophisticated
  // For now, create matches from citations
  return citations.map((citation, idx) => {
    const confidence = citation.confidence || response.evidence_confidence || response.confidence?.avg || 0;
    return {
      proposalId: citation.evidence_id || citation.id?.toString() || `proposal-${idx}`,
      filename: citation.source || 'Unknown Proposal',
      section: citation.section_path || 'Unknown Section',
      page: citation.page || 0,
      excerpt: citation.content?.substring(0, 300) + '...' || answer.substring(idx * 100, (idx + 1) * 100) + '...',
      score: confidence,
      confidence_level: 
        confidence >= 0.75 ? 'high' as const :
        confidence >= 0.60 ? 'medium' as const :
        'low' as const,
      win_status: 'Unknown' as const, // Would come from artifact metadata
      service_line: 'Unknown', // Would come from artifact metadata
      evidence_id: citation.evidence_id || citation.id?.toString() || '',
    };
  });
}

/**
 * Generate RFP proposal draft
 * Returns the report ID when the job completes
 */
export async function generateProposalDraft(
  projectId: string,
  request: {
    sections: string[];
    evidence_sources: string[]; // Artifact IDs
    word_limit?: number;
    rfp_artifact_id?: string;
    client_name?: string;
  }
): Promise<string> {
  // Convert sections to questions format required by ReportJobRequest
  const questions = request.sections.map(section => ({
    section,
    question: `Generate ${section} section for RFP proposal`,
  }));

  // Use core report generation with RFP-specific parameters
  const jobRequest = {
    project_id: projectId,
    report_type: 'custom' as const, // Use 'custom' since 'rfp_proposal' is not in the enum
    client_name: request.client_name || 'RFP Proposal',
    questions,
    metadata: {
      rfp_artifact_id: request.rfp_artifact_id,
      evidence_sources: request.evidence_sources,
      word_limit: request.word_limit,
      citation_style: 'inline',
      tone: 'professional',
      policy_pack_id: 'keystone-rfp-agent-v1',
      report_type_override: 'rfp_proposal', // Store actual type in metadata
    },
    enable_cache: true,
  };

  // Submit job and wait for completion
  const jobStatus = await submitReportJob(jobRequest);
  
  // Return report ID from job output
  if (jobStatus.status === 'completed' && jobStatus.output_data?.report_id) {
    return jobStatus.output_data.report_id;
  }
  
  // If job is still running, return job ID (caller should poll)
  // In production, this would poll until complete
  throw new Error(`Report job ${jobStatus.id} is ${jobStatus.status}. Poll for completion.`);
}

/**
 * Optimize word count for proposal
 */
export async function optimizeWordCount(
  reportId: string,
  targetWordCount: number,
  optimizationLevel: 'aggressive' | 'moderate' | 'conservative' = 'moderate',
  sectionIds?: string[],
  preservationPriorities?: {
    citations?: boolean;
    requirements?: boolean;
    technical_accuracy?: boolean;
    examples?: boolean;
  }
): Promise<{
  job_id?: string;
  status: string;
  original_count?: number;
  optimized_count?: number;
  reduction_percentage?: number;
  sections_optimized?: string[];
  changes?: Array<{
    section_id: string;
    section_name: string;
    original_text: string;
    optimized_text: string;
    word_reduction: number;
    confidence: number;
    explanation: string;
    impact_assessment: {
      citations_preserved: boolean;
      requirements_covered: boolean;
      technical_accuracy: string;
    };
    original_count: number;
    optimized_count: number;
  }>;
  sections?: Array<any>;
  optimization_session_id?: string;
  session_id?: string;
}> {
  const apiClient = getAPIClient();
  const response = await apiClient.post(`/reports/${reportId}/word-count/optimize`, {
    target_word_count: targetWordCount,
    optimization_level: optimizationLevel,
    section_ids: sectionIds,
    preservation_priorities: preservationPriorities,
  });
  return response;
}

/**
 * Staff Matching API Functions
 */

/**
 * Get all staff matching rules
 */
export async function getStaffMatchingRules(
  projectId: string
): Promise<StaffMatchingRule[]> {
  const apiClient = getAPIClient();
  const response = await apiClient.get('/api/staff/rules', {
    params: { project_id: projectId },
  });
  return response.rules || [];
}

/**
 * Create or update staff matching rules
 */
export async function createStaffMatchingRule(
  projectId: string,
  rule: StaffMatchingRule
): Promise<StaffMatchingRule> {
  const apiClient = getAPIClient();
  const response = await apiClient.post('/api/staff/rules', {
    project_id: projectId,
    rules: [rule],
  });
  return response.rules?.[0] || rule;
}

/**
 * Update multiple staff matching rules
 */
export async function updateStaffMatchingRules(
  projectId: string,
  rules: StaffMatchingRule[]
): Promise<{ rules: StaffMatchingRule[]; total_rules: number }> {
  const apiClient = getAPIClient();
  const response = await apiClient.post('/api/staff/rules', {
    project_id: projectId,
    rules,
  });
  return response;
}

/**
 * Delete a staff matching rule
 */
export async function deleteStaffMatchingRule(
  ruleId: string
): Promise<void> {
  const apiClient = getAPIClient();
  await apiClient.delete(`/api/staff/rules/${ruleId}`);
}

/**
 * Match staff members against an RFP requirement
 */
export async function matchStaff(
  projectId: string,
  requirement: {
    id?: string;
    title?: string;
    required_designations?: string[];
    min_experience?: number;
    required_expertise?: string[];
    location_preference?: {
      province?: string;
      city?: string;
    };
    availability_required?: boolean;
    role_level?: 'junior' | 'mid' | 'senior' | 'principal';
    project_keywords?: string[];
    [key: string]: any;
  },
  staffMemberIds?: string[]
): Promise<StaffMatchResult[]> {
  const apiClient = getAPIClient();
  const response = await apiClient.post('/api/staff/match', {
    project_id: projectId,
    requirement,
    staff_member_ids: staffMemberIds,
  });
  return response.results || [];
}

/**
 * Certification Deduplication API Functions
 */

export interface CertificationDeduplicationRequest {
  project_id: string;
  dry_run: boolean;
}

export interface CertificationDeduplicationResponse {
  total_entities: number;
  unique_certifications: number;
  entities_to_delete: number;
  duplicate_groups: number;
  top_duplicates: Array<{
    certification_name: string;
    issuing_body?: string;
    duplicate_count: number;
  }>;
  dry_run: boolean;
}

/**
 * Deduplicate certifications (non-streaming)
 */
export async function deduplicateCertifications(
  projectId: string,
  dryRun: boolean = true
): Promise<CertificationDeduplicationResponse> {
  const apiClient = getAPIClient();
  const response = await apiClient.post('/api/certifications/deduplicate', {
    project_id: projectId,
    dry_run: dryRun,
  });
  return response;
}

/**
 * Get certification statistics
 */
export async function getCertificationStats(
  projectId: string
): Promise<{
  total: number;
  active: number;
  expiring_soon: number;
  expired: number;
}> {
  const apiClient = getAPIClient();
  const response = await apiClient.get('/api/certifications/stats', {
    params: { project_id: projectId },
  });
  return response;
}

/**
 * Deduplicate certifications with streaming progress
 * Returns an async generator that yields progress events
 */
export async function* deduplicateCertificationsStream(
  projectId: string,
  dryRun: boolean = true
): AsyncGenerator<{
  type: 'progress' | 'complete' | 'error';
  data: any;
}, void, unknown> {
  const { getAPIBaseUrl, getAuthHeaders } = await import('@tracekit/core');
  const baseUrl = getAPIBaseUrl();
  const authHeaders = getAuthHeaders();
  
  const response = await fetch(`${baseUrl}/api/certifications/deduplicate/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
      ...authHeaders,
    },
    body: JSON.stringify({
      project_id: projectId,
      dry_run: dryRun,
    }),
  });
  
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }
  
  const reader = response.body?.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let currentEventType = '';
  
  if (!reader) {
    throw new Error('Response body is not readable');
  }
  
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    
    for (const line of lines) {
      if (line.startsWith('event: ')) {
        currentEventType = line.substring(7).trim();
        continue;
      }
      
      if (line.startsWith('data: ')) {
        try {
          const data = JSON.parse(line.substring(6));
          
          if (currentEventType === 'progress') {
            yield { type: 'progress', data };
          } else if (currentEventType === 'complete') {
            yield { type: 'complete', data };
          } else if (currentEventType === 'error') {
            yield { type: 'error', data };
          }
        } catch (e) {
          console.error('Error parsing SSE data:', e, line);
        }
        currentEventType = '';
      }
    }
  }
}

/**
 * Apply word count optimizations to a report
 */
export async function applyOptimization(
  reportId: string,
  optimizationSessionId: string,
  acceptedSectionIds?: string[]
): Promise<{
  status: string;
  updated_sections: string[];
  total_sections_updated: number;
}> {
  const apiClient = getAPIClient();
  const response = await apiClient.post(`/reports/${reportId}/word-count/apply`, {
    optimization_session_id: optimizationSessionId,
    accepted_section_ids: acceptedSectionIds,
  });
  return response;
}

/**
 * Rollback word count optimizations to previous version
 */
export async function rollbackOptimization(
  reportId: string,
  optimizationSessionId: string
): Promise<{
  status: string;
  message: string;
}> {
  const apiClient = getAPIClient();
  const response = await apiClient.post(`/reports/${reportId}/word-count/rollback`, {
    optimization_session_id: optimizationSessionId,
  });
  return response;
}

/**
 * Validate word count compliance for a report
 */
export async function validateWordCount(
  reportId: string
): Promise<{
  is_compliant: boolean;
  total_word_count: number;
  word_limit?: number;
  section_word_counts?: Record<string, number>;
  section_limits?: Record<string, number>;
  violations: {
    total: Array<{
      type: string;
      current: number;
      limit: number;
      excess: number;
      message: string;
    }>;
    sections: Array<{
      section: string;
      current: number;
      limit: number;
      excess: number;
      message: string;
    }>;
  };
}> {
  const apiClient = getAPIClient();
  const response = await apiClient.get(`/reports/${reportId}/word-count/validate`);
  return response;
}

/**
 * Update word count limits for a report
 */
export async function updateWordLimits(
  reportId: string,
  limits: {
    total_limit?: number;
    section_limits?: Record<string, number>;
  }
): Promise<{
  status: string;
  word_limit?: number;
  section_limits?: Record<string, number>;
}> {
  const apiClient = getAPIClient();
  const response = await apiClient.put(`/reports/${reportId}/word-count/limits`, {
    total_limit: limits.total_limit,
    section_limits: limits.section_limits,
  });
  return response;
}

/**
 * Staff Assignment to Report Functions
 */

/**
 * Assign a staff member to a report (proposal)
 */
export async function assignStaffToReport(
  reportId: string,
  staffMemberId: string,
  options?: {
    role?: string;
    assignedHoursPerWeek?: number;
    notes?: string;
    assignmentMetadata?: Record<string, any>;
  }
): Promise<ReportStaffAssignment> {
  const apiClient = getAPIClient();
  const response = await apiClient.post('/api/rfp/staff/assign-to-report', {
    report_id: reportId,
    staff_member_id: staffMemberId,
    role: options?.role,
    assigned_hours_per_week: options?.assignedHoursPerWeek,
    notes: options?.notes,
    assignment_metadata: options?.assignmentMetadata,
  });
  return response;
}

/**
 * Get all staff assignments for a report
 */
export async function getReportStaffAssignments(
  reportId: string
): Promise<ReportStaffAssignmentsResponse> {
  const apiClient = getAPIClient();
  const response = await apiClient.get(`/api/rfp/staff/report-assignments/${reportId}`);
  return response;
}

/**
 * Update a staff assignment
 */
export async function updateStaffAssignment(
  assignmentId: string,
  updates: {
    role?: string;
    assignedHoursPerWeek?: number;
    status?: 'active' | 'completed' | 'cancelled';
    notes?: string;
    assignmentMetadata?: Record<string, any>;
  }
): Promise<ReportStaffAssignment> {
  const apiClient = getAPIClient();
  const response = await apiClient.patch(`/api/rfp/staff/report-assignments/${assignmentId}`, {
    role: updates.role,
    assigned_hours_per_week: updates.assignedHoursPerWeek,
    status: updates.status,
    notes: updates.notes,
    assignment_metadata: updates.assignmentMetadata,
  });
  return response;
}

/**
 * Remove a staff assignment from a report
 */
export async function removeStaffAssignment(
  assignmentId: string
): Promise<void> {
  const apiClient = getAPIClient();
  await apiClient.delete(`/api/rfp/staff/report-assignments/${assignmentId}`);
}

// ─── Workplan Template Modification (Phase 2/3 Step 4) ─────────────────────────

export interface TemplateModJobRequest {
  project_id: string;
  generation_phase: 2 | 3;
  generation_prompt: string;
  artifact_ids: string[];
  base_template: {
    templateId?: string;
    templateName?: string;
    category?: string;
    categoryName?: string;
    sections: Array<{ id: string; name: string; description?: string; level: string; parentId: string | null; order: number }>;
    questions: Array<{ id: string; sectionId: string; sectionName: string; question: string; type?: string; order: number }>;
  };
}

export interface TemplateModJobSubmitResponse {
  job_id: string;
  status: string;
}

export interface TemplateModJobStatusResponse {
  job_id: string;
  status: string;
  progress_data?: { status_message?: string; step?: string };
  output_data?: { modified_template?: any; modification_notes?: string };
  error_message?: string;
}

export async function submitTemplateModificationJob(
  request: TemplateModJobRequest
): Promise<TemplateModJobSubmitResponse> {
  const apiClient = getAPIClient();
  const res = await apiClient.post<TemplateModJobSubmitResponse>(
    '/api/rfp/workplan-template-modification/job',
    request
  );
  return res;
}

export async function getTemplateModificationJobStatus(
  jobId: string
): Promise<TemplateModJobStatusResponse> {
  const apiClient = getAPIClient();
  const res = await apiClient.get<TemplateModJobStatusResponse>(
    `/api/rfp/workplan-template-modification/job/${jobId}`
  );
  return res;
}

/** Workplan template matching — rank templates by relevance to artifacts + prompt */
export interface TemplateMatchRequest {
  artifact_names: string[];
  generation_prompt: string;
}

export interface TemplateMatchResult {
  artifact_id: string;
  score: number;
}

export interface TemplateMatchResponse {
  results: TemplateMatchResult[];
  processing_time_ms: number;
}

export async function matchWorkplanTemplates(
  request: TemplateMatchRequest
): Promise<TemplateMatchResponse> {
  const apiClient = getAPIClient();
  const res = await apiClient.post<TemplateMatchResponse>(
    '/api/rfp/workplan-templates/match',
    request
  );
  return res;
}
